import axios from "axios";
import {uuid} from "../utils";
import {cloneDeep, debounce} from "lodash";
import moment from "moment-timezone";

export default {
    jwt: null,
    user: null,
    lastAck: false,
    renewRate: 41,
    heartbeatRate: 10,
    registering: false,
    connectionId: null,
    ws: null,
    connected: false,
    connectionTimout: null,
    showAsDisconnectedTimeout: null,
    heartbeatTimeout: null,
    heartbeatDisconnectTimeout: null,
    renewTimeout: null,
    refreshTokenTimeout: null,
    sendStoragePending: false,
    localStateStore: {},
    aggregateListeners: [],
    aggregateRegister: [],
    active: true,
    autoLogoutTimeout: false,
    lastRenewed: moment.tz('Europe/London'),
    lastActivity: moment.tz('Europe/London').toISOString(),
    async refreshToken() {
        await axios.get(route('api.v2.organization.user'))
            .catch((e) => {
                // try 1 second later
                setTimeout(async () => {
                    await User.refreshToken()
                }, 1000);
            })
            .then(async (response) => {
                if (response) {
                    User.jwt = response.data.jwt;
                    clearTimeout(User.refreshTokenTimeout)
                    User.refreshTokenTimeout = setTimeout(async () => {
                        await User.refreshToken()
                    }, 60000);
                } else {
                    await User.register();
                }
            })
    },
    async refresh() {
        if (User.user) {
            let response = await Http.get(route('api.v2.organization.user'));
            if (response) {
                User.jwt = response.jwt;
                User.user = response
                User.user.loggedOut = false
                if (Array.isArray(User.user.remoteStorage)) {
                    User.user.remoteStorage = {}
                }
            }
        }
    },
    async switchOrganization(organizationId) {
        window.location.href = this.user.memberOf.find((i) => i.id === organizationId).url
    },
    waitForMe(ms) {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve('')
            }, ms);
        })
    },
    async register() {
        if (!User.user) {
            if (!User.registering) {
                User.registering = true;
                let response = await Http.get(route('api.v2.organization.user'));
                if (response) {
                    User.jwt = response.jwt;
                    User.user = response
                    User.user.loggedOut = false
                    if (Array.isArray(User.user.remoteStorage)) {
                        User.user.remoteStorage = {}
                    }
                    User.connectionId = uuid()
                    await User.connect()
                    User.registerAggregate({type: 'User', id: User.user.id}, (e) => {
                        if (e.operation === 'updateAggregate') {
                            Object.assign(User.user.connections, e.aggregate.data.connections)
                        }
                    }, true)
                    await User.refreshToken()
                    window.Bus.on('activity', () => {
                        User.setLastActivity();
                    });
                    User.registering = false
                } else {
                    setTimeout(async () => {
                        User.user = false;
                        User.registering = false;
                        await User.register()
                    }, 2000);
                }
            } else {
                await User.waitForMe(300)
                await User.register()
            }
        }
    },
    wsOpen() {
        clearTimeout(User.showAsDisconnectedTimeout)
        User.clearTimeouts()
        User.connected = true
        User.renew()
        User.heartbeat()
        User.attachDocumentEvents()
        if (User.sendStoragePending) {
            User.persistStorage()
            User.sendStoragePending = false;
        }
    },
    wsError() {
        User.wsClose();
    },
    wsClose() {
        User.clearTimeouts()
        User.connectionTimout = setTimeout(async () => {
            await User.connect()
        }, 500);
        clearTimeout(User.showAsDisconnectedTimeout)
        User.showAsDisconnectedTimeout = setTimeout(() => {
            User.connected = false;
        }, 2500);
    },
    wsMessage(e) {
        let op = JSON.parse(e.data)
        if (op.ack) {
            clearTimeout(User.heartbeatDisconnectTimeout);
            if ((op.ack !== User.lastAck) && (op.ack !== User.previousLastAck)) {
                User.connected = false
                User.ws.close();
            } else {
                User.connected = true
                User.previousLastAck = User.lastAck
            }
            return;
        }
        if (op.operation === 'updateAggregate') {
            if ((op.aggregate.type === 'User') && (op.aggregate.id === User.user.id)) {
                Object.assign(User.user, op.aggregate.data);
            }
        } else if ((op.aggregate.type === 'User') && (op.aggregate.id === User.user.id) && (op.event === 'UserLoggedOut')) {
            User.user.sessions = User.user.sessions.filter((i) => i.id !== op.data);
        }

        let key = op.aggregate.type + '|' + op.aggregate.id + '|' + op.event

        if (User.aggregateListeners[key]) {
            User.aggregateListeners[key].forEach((l) => l(op))
        }
    },
    createWs() {
        let hostname = location.hostname;
        User.ws = new WebSocket("wss://" + hostname + ":9502")
        User.ws.addEventListener("open", User.wsOpen)
        User.ws.addEventListener("error", User.wsError)
        User.ws.addEventListener("message", User.wsMessage)
        User.ws.addEventListener("close", User.wsClose)
    },
    destroyWs() {
        User.ws.removeEventListener("open", User.wsOpen)
        User.ws.removeEventListener("error", User.wsError)
        User.ws.removeEventListener("message", User.wsMessage)
        User.ws.removeEventListener("close", User.wsClose)
        User.ws.close()
    },
    async connect() {
        if (User.ws && User.ws.readyState !== 2) {
            User.destroyWs();
            User.ws = null;
        }
        if (!User.ws) {
            User.clearTimeouts()
            User.createWs()
        }
    },
    clearTimeouts() {
        if (User.connectionTimout) {
            clearTimeout(User.connectionTimout)
        }
        if (User.renewTimeout) {
            clearTimeout(User.renewTimeout)
        }
        clearTimeout(User.heartbeatTimeout)
    },
    getCookie(name) {
        let match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) return match[2];
    },
    renew() {
        if (User.ws && User.user && (User.ws.readyState === WebSocket.OPEN)) {
            if (User.lastRenewed.unix() !== moment.tz('Europe/London').unix()) {
                User.lastRenewed = moment.tz('Europe/London')
                User.refreshExpiredToken();
                User.ws.send(JSON.stringify({
                    'operation': 'renew',
                    'user': this.getUserObject(),
                    'auth': User.jwt,
                }));
            }
        }
        clearTimeout(User.renewTimeout)
        User.renewTimeout = setTimeout(() => {
            User.renew()
            // PING every XXX seconds to keep it alive
        }, User.renewRate * 1000)
    },
    async reconnectIfRequired() {
        if (!User.connected) {
            User.destroyWs();
            User.ws = null;
            await User.connect();
        }
    },
    heartbeat() {
        if (User.ws && User.user && (User.ws.readyState === WebSocket.OPEN)) {
            clearTimeout(User.heartbeatDisconnectTimeout)
            let newAck = uuid();
            User.refreshExpiredToken();
            User.ws.send(JSON.stringify({
                'operation': 'ack',
                'user': User.getUserObject(),
                'auth': User.jwt,
                'ack': newAck
            }));
            User.previousLastAck = User.lastAck;
            User.lastAck = newAck;
            User.heartbeatDisconnectTimeout = setTimeout(async () => {
                User.destroyWs();
                await User.connect();
            }, (User.heartbeatRate * 1000) - 1000)
        } else if (User.ws && User.user && (User.ws.readyState === WebSocket.CONNECTING)) {
            // do nothing - we are connecting.....
        } else if (User.user) {
            User.connected = false;
            User.connect();
        }
        clearTimeout(User.heartbeatTimeout)
        User.heartbeatTimeout = setTimeout(() => {
            User.heartbeat()
        }, User.heartbeatRate * 1000)
    },
    refreshExpiredToken() {
        let parseJwt = function (token) {
            let base64Url = token.split('.')[1];
            let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            let jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            return JSON.parse(jsonPayload);
        }
        if (moment.tz(parseJwt(User.user.jwt)['valid_until'], 'Europe/London').isBefore(moment.tz('Europe/London').add(1, 'minute'))) {
            Promise.resolve(axios.get(route('api.v2.organization.user'))).then((response) => {
                if (response) {
                    User.jwt = response.data.jwt;
                }
            })
        }
    },
    push(operation, data) {
        User.refreshExpiredToken();
        if (User.ws && User.user && (User.ws.readyState === WebSocket.OPEN)) {
            User.ws.send(JSON.stringify({
                'operation': operation,
                'user': User.getUserObject(),
                'data': data,
                'auth': User.jwt,
            }));
        }
    },
    isAggregateListenerRegistered(aggregate, listener) {
        if (User.aggregateListeners[aggregate.type + '|' + aggregate.id + '|' + aggregate.event]) {
            return User.aggregateListeners[aggregate.type + '|' + aggregate.id + '|' + aggregate.event].indexOf(listener) !== -1
        }
        return false
    },
    isAggregateRegistered(aggregate) {
        return User.aggregateRegister.indexOf(aggregate.type + '|' + aggregate.id + '|' + aggregate.event) !== -1
    },
    registerAggregates(aggregates, listener) {
        if (User.ws && User.user && (User.ws.readyState === WebSocket.OPEN)) {
            let newAggregates = [];
            aggregates.forEach((a) => {
                if (!User.isAggregateRegistered(a)) {
                    newAggregates.push(a)
                }
            });
            if (newAggregates.length) {
                User.refreshExpiredToken();
                User.ws.send(JSON.stringify({
                    'operation': 'registerAggregates',
                    'aggregates': newAggregates,
                    'user': this.getUserObject(),
                    'auth': User.jwt,
                }));
            }
            aggregates.forEach((a) => {
                if (!User.isAggregateRegistered(a)) {
                    User.aggregateRegister.push(a.type + '|' + a.id + '|' + a.event)
                }
                if (User.isAggregateListenerRegistered(a, listener)) {
                    return
                }
                if (User.aggregateListeners[a.type + '|' + a.id + '|' + a.event]) {
                    User.aggregateListeners[a.type + '|' + a.id + '|' + a.event].push(listener)
                } else {
                    User.aggregateListeners[a.type + '|' + a.id + '|' + a.event] = [listener];
                }
            })
        } else {
            // try again after 200ms
            setTimeout(() => User.registerAggregates(aggregates, listener), 200)
        }
    },
    registerAggregate(aggregate, listener) {
        if (User.ws && User.user && (User.ws.readyState === WebSocket.OPEN)) {
            if (!User.isAggregateRegistered(aggregate)) {
                User.refreshExpiredToken();
                User.aggregateRegister.push(aggregate.type + '|' + aggregate.id + '|' + aggregate.event)
                User.ws.send(JSON.stringify({
                    'operation': 'registerAggregate',
                    'aggregate': {type: aggregate.type, id: aggregate.id, event: aggregate.event},
                    'user': User.getUserObject(),
                    'auth': User.jwt,
                }));
            }
            if (User.isAggregateListenerRegistered(aggregate, listener)) {
                return
            }
            if (User.aggregateListeners[aggregate.type + '|' + aggregate.id + '|' + aggregate.event]) {
                User.aggregateListeners[aggregate.type + '|' + aggregate.id + '|' + aggregate.event].push(listener)
            } else {
                User.aggregateListeners[aggregate.type + '|' + aggregate.id + '|' + aggregate.event] = [listener];
            }
        } else {
            // try again after 200ms
            setTimeout(() => User.registerAggregate(aggregate, listener), 200)
        }
    },
    getFromStorage(key) {
        let o = User.user.remoteStorage[key];
        if (o !== undefined) {
            return o
        }
        return null
    },
    getStorageKeys() {
        return Object.keys(User.user.remoteStorage);
    },
    clearStorageKey(key) {
        let storage = cloneDeep(User.user.remoteStorage);
        if (storage.hasOwnProperty(key)) {
            delete storage[key];
            if (User.ws && (User.ws.readyState === WebSocket.OPEN)) {
                User.refreshExpiredToken();
                User.ws.send(JSON.stringify({
                    'operation': 'updateStorage',
                    'user': User.getUserObject(),
                    'auth': User.jwt,
                }));
            } else {
                User.sendStoragePending = true;
            }
        }
    },
    debouncedPersistStorage: debounce(() => User.persistStorage(), 100, {leading: false}),
    persistStorage() {
        if (User.ws && (User.ws.readyState === WebSocket.OPEN)) {
            User.refreshExpiredToken();
            User.ws.send(JSON.stringify({
                'operation': 'updateStorage',
                'user': this.getUserObject(),
                'data': User.user.remoteStorage,
                'auth': User.jwt,
            }));
        } else {
            User.sendStoragePending = true;
        }
    },
    putIntoStorage(key, value) {
        if (User.user.remoteStorage[key] !== value) {
            User.user.remoteStorage[key] = value;
            User.debouncedPersistStorage()
        }
    },
    getUserObject() {
        return {
            id: User.user.id,
            context: User.user.context,
            connection: User.connectionId,
            active: User.active,
            lastActivity: User.lastActivity,
            sessionId: User.getSessionId(),
        }
    },
    setLastActivity() {
        let current = moment.tz(User.lastActivity, 'Europe/London');
        let newOne = moment.tz('Europe/London');
        if ((newOne.unix() - current.unix() > 5) || (User.countdown < 60 * 5)) {
            User.lastActivity = newOne.toISOString()
            User.reconnectIfRequired()
            clearTimeout(User.renewTimeout)
            User.renew()
        }
    },
    getSessionId() {
        return this.getCookie('insync_session_id');
    },
    visibilityChange(event) {
        if (document.visibilityState === "visible") {
            User.reconnectIfRequired();
        }
    },
    attachDocumentEvents() {
        document.removeEventListener('mousemove', User.setLastActivity);
        document.removeEventListener('keyup', User.setLastActivity);
        document.removeEventListener('visibilitychange', User.reconnectIfRequired);
        document.removeEventListener('focus', User.visibilityChange);
        document.addEventListener('mousemove', User.setLastActivity);
        document.addEventListener('keyup', User.setLastActivity);
        document.addEventListener('visibilitychange', User.visibilityChange);
        document.addEventListener('focus', User.reconnectIfRequired);
        clearTimeout(User.autoLogoutTimeout)
        let fn = () => {
            let sessionId = User.getSessionId();
            if (sessionId) {
                let session = User.user.sessions.find((i) => i.id === sessionId);
                if (session) {
                    User.countdown = moment.tz(session.expires, 'Europe/London').unix() - moment.tz('Europe/London').unix()
                    if (User.countdown < 60 * 5) {
                        User.reconnectIfRequired()
                        clearTimeout(User.renewTimeout)
                        User.renew()
                    }
                } else {
                    User.countdown = -2
                }
            } else {
                User.countdown = -2
            }
            if ((User.countdown <= -2) && (document.visibilityState === "visible")) {
                User.clearTimeouts()
                if (window.parent) {
                    window.parent.location = window.parent.location.origin + window.parent.location.pathname + window.parent.location.search + (window.parent.location.search ? '&' : '?') + 'reloadTime=' + Date.now().toString() + window.parent.location.hash
                } else {
                    window.location = window.location.origin + window.location.pathname + window.location.search + (window.location.search ? '&' : '?') + 'reloadTime=' + Date.now().toString() + window.location.hash
                }
                return;
            }
            User.autoLogoutTimeout = setTimeout(fn, 1000)
        }
        setTimeout(fn, 2000)
    },
    hasPermission(context, action) {
        // Bypass for legacy id
        if (this.user.legacyId) {
            return true
        }

    }
}
